import { useMutation, useQueryClient } from '@tanstack/react-query'
import { formatISO } from 'date-fns'
import { produce } from 'immer'
import { v4 as uuidV4 } from 'uuid'
import { OpalApiResponseDocument } from '../../../schema'
import { stories } from '../../../v2/stories'
import { globalQueryKeys } from '../../queryKeys'

type CreateStoryV2 = OpalApiResponseDocument<'CreateStoryV2', 201>
type StoriesResponse = OpalApiResponseDocument<'ReadStoriesV2', 200>

type StoryAttributes = CreateStoryV2['data']['attributes']
type StoryRelationships = NonNullable<CreateStoryV2['data']['relationships']>
type StoryBrand = NonNullable<
  NonNullable<StoryRelationships['brand']>['data']
>['id']

type MutationParams = {
  name: StoryAttributes['name']
  scheduledStartAt: StoryAttributes['scheduled_start_at']
  scheduledEndAt: StoryAttributes['scheduled_end_at']
  workspaceId: StoryBrand
  isPublic?: boolean
}

export const useCreateStory = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      name,
      scheduledStartAt,
      scheduledEndAt,
      workspaceId,
      isPublic,
    }: MutationParams) => {
      return stories.createSingle({
        requestBody: {
          data: {
            type: 'story',
            attributes: {
              name: name,
              scheduled_start_at: scheduledStartAt,
              scheduled_end_at: scheduledEndAt,
              ...(isPublic !== undefined && { is_public: isPublic }),
            },
            relationships: {
              brand: {
                data: {
                  id: workspaceId, // Must be legacy workspace ID
                  type: 'brand',
                },
              },
            },
          },
        },
      })
    },
    onMutate: ({
      name,
      scheduledStartAt,
      scheduledEndAt,
      workspaceId,
    }: MutationParams) => {
      queryClient.setQueryData<StoriesResponse>(
        globalQueryKeys.workspaceStories(parseInt(workspaceId, 10)),
        (storyDocument) => {
          if (!storyDocument) return storyDocument

          // Note: `newStory` has a prefixed `temp-` to its `id` value used to check
          // the story's `id` for validity before allowing users to interact with it.
          const newStory = {
            id: 'temp-' + uuidV4(),
            type: 'story' as const,
            attributes: {
              uuid: 'temp-' + uuidV4(),
              name: name,
              scheduled_start_at: scheduledStartAt,
              scheduled_end_at: scheduledEndAt,
              asset_references_count: null,
              color: null,
              created_at: formatISO(new Date()),
              description: null,
              is_shared: null,
              is_visible: true,
              legacy_is_private: null,
              members_count: null,
              moments_count: null,
              strategies_count: null,
              updated_at: '',
            },
            relationships: {
              brand: {
                data: {
                  id: workspaceId,
                  type: 'brand' as const,
                },
              },
            },
          }

          return produce(storyDocument, (draftStoryDocument) => {
            if (!draftStoryDocument) return draftStoryDocument
            draftStoryDocument.data.push(newStory)

            return draftStoryDocument
          })
        }
      )
    },
    onSuccess: (_, { workspaceId }) => {
      queryClient.invalidateQueries(
        globalQueryKeys.workspaceStories(parseInt(workspaceId, 10))
      )
    },
  })
}
